import { Pagination } from "@mui/material"
import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { useRecoilState } from "recoil"
import { TableHeaderItem } from "src/components/mypage/TableHeaderItem"
import { useLoginContext } from "src/hooks/useLoginContext"
import { RESPONSE_CODE, SNACKBAR_TYPES, SORT_ORDER } from "src/modules/const"
import { Contactus, ContactusService } from "src/openapi"
import { ShowToast } from "src/states/showToast"
import { Box, Grid, Typography } from "src/UILibrary"
import { ContactTableItem } from "./ContactTableItem"

const SORT_BY = {
  DATE: "updated_at",
  NAME: "name",
  EMAIL: "email",
  COMPANY: "company",
  CONTEXT: "context",
}

const PAGE_SIZE = 10

export const ContactListPage = () => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.inbox" })

  const isPC = useMediaQuery({ query: "(min-width: 1400px)" })
  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  const { accessToken, checkLoginStatus, initialized } = useLoginContext()

  const [, setShowToast] = useRecoilState(ShowToast)

  const [contacts, setContacts] = useState<Contactus[]>([])
  const [pageNum, setPageNum] = useState<number>(1)
  const [total, setTotal] = useState<number>(0)
  const [currentSort, setCurrentSort] = useState<string>(SORT_BY.DATE)
  const [currentSortOrder, setCurrentSortOrder] = useState<string>(SORT_ORDER.DESCENDONG)

  let headers: any[] = [
    {
      size: isPC ? 1.5 : isTablet ? 2 : 1.5,
      name: t("date"),
      sortby: SORT_BY.DATE,
    },
    {
      size: isPC ? 2 : isTablet ? 2 : 2,
      name: t("name"),
      sortby: SORT_BY.NAME,
    },
    {
      size: isPC ? 2.5 : isTablet ? 3 : 2.5,
      name: t("email"),
      sortby: SORT_BY.EMAIL,
    },
    {
      size: isPC ? 2 : isTablet ? 2 : 2,
      name: t("company"),
      sortby: SORT_BY.COMPANY,
    },
    {
      size: isPC ? 4 : isTablet ? 3 : 2,
      name: t("context"),
      sortby: SORT_BY.CONTEXT,
    },
  ]

  const getContacts = (sort: string, sortOrder: string) => {
    ContactusService.contactusList(
      accessToken,
      (pageNum - 1) * PAGE_SIZE,
      PAGE_SIZE,
      sort,
      sortOrder
    )
      .then((res) => {
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setContacts(res.data?.contactus as Contactus[])
          setTotal(res.data?.count as number)
        } else {
          setShowToast({ show: true, text: t("failed_get_consults"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("failed_get_consults"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const onSortChanged = (sorter: string, asc: boolean) => {
    getContacts(sorter, asc ? SORT_ORDER.ASCENDING : SORT_ORDER.DESCENDONG)
    setCurrentSort(sorter)
    setCurrentSortOrder(asc ? SORT_ORDER.ASCENDING : SORT_ORDER.DESCENDONG)
  }

  useEffect(() => {
    if (!initialized) {
      return
    }

    getContacts(currentSort, currentSortOrder)
  }, [pageNum, initialized])

  return (
    <Box
      sx={{
        overflowY: "scroll",
      }}
    >
      {isTablet ? (
        <Grid container sx={{ mt: "0px", height: "32px", borderBottom: "1px dotted #D4D3D7" }}>
          {headers.map((header, index) => (
            <TableHeaderItem
              key={index}
              name={header.name}
              size={header.size}
              sortby={header.sortby}
              onSortChanged={onSortChanged}
            />
          ))}
        </Grid>
      ) : (
        <></>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          ...(isTablet
            ? { height: "auto" }
            : { width: "100%", position: "absolute", top: 60, bottom: 120 }),
        }}
      >
        {contacts.length === 0 ? (
          <Typography.SubTitleNormal
            sx={{ my: "50px", mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
          >
            {t("no_data")}
          </Typography.SubTitleNormal>
        ) : (
          <>
            {contacts.map((contact, index) => (
              <ContactTableItem key={index} item={contact} />
            ))}
            <Box display="flex" sx={{ my: "20px", width: "100%" }}>
              <Pagination
                count={Math.ceil(total / PAGE_SIZE)}
                variant="outlined"
                color="primary"
                onChange={(event, page) => setPageNum(page)}
                sx={{ mx: "auto" }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
